/* Fleet Packs custom dropdowns.

   Progressive enhancement: the native <select> stays in the DOM and keeps the
   form value, so the form still submits without JavaScript and any existing
   change listeners keep working. We only hide it and drive it from a styled
   button + listbox.

   Public API (window.FleetPacksSelect):
     enhance(root)  upgrade every eligible select inside root
     reset(root)    strip the custom UI back to plain selects (used before
                    cloning a vehicle row, so the clone starts clean)
     refresh(root)  re-read select values after code changed them directly
*/
(() => {
  const SELECTOR = 'select.fp-form__select, select[data-fp-select]';
  let counter = 0;

  const isOpen = (wrap) => wrap.classList.contains('is-open');

  function optionsOf(select) {
    return [...select.options].filter((option) => !option.disabled);
  }

  function labelFor(select) {
    const option = select.selectedOptions[0];
    return option ? option.textContent.trim() : '';
  }

  function close(wrap, focusToggle = false) {
    if (!isOpen(wrap)) return;
    wrap.classList.remove('is-open');
    const toggle = wrap.querySelector('.fp-select__toggle');
    toggle.setAttribute('aria-expanded', 'false');
    const list = wrap.querySelector('.fp-select__list');
    list.hidden = true;
    if (wrap.classList.contains('fp-select--fixed')) {
      wrap.classList.remove('fp-select--fixed');
      ['position', 'top', 'left', 'right', 'bottom', 'minWidth', 'maxHeight']
        .forEach((name) => { list.style[name] = ''; });
    }
    if (focusToggle) toggle.focus();
  }

  /* A panel that scrolls clips anything inside it, which cut the options off in
     the vehicles grid. When an ancestor can clip, the list is measured against
     the viewport instead and follows its field while anything scrolls. */
  function clippingAncestor(element) {
    let node = element.parentElement;
    while (node && node !== document.body) {
      const style = getComputedStyle(node);
      if (/auto|scroll|hidden/.test(`${style.overflow} ${style.overflowX} ${style.overflowY}`)) return node;
      node = node.parentElement;
    }
    return null;
  }

  function placeList(wrap) {
    const list = wrap.querySelector('.fp-select__list');
    const rect = wrap.getBoundingClientRect();
    const gap = 6;
    const edge = 8; // never sit flush against the window
    const below = window.innerHeight - rect.bottom - gap - edge;
    const above = rect.top - gap - edge;
    // Cleared first, or a reposition would measure the width we set last time.
    list.style.minWidth = '';
    list.style.maxHeight = '';
    const floor = parseFloat(getComputedStyle(list).minWidth) || 0;
    const wanted = Math.min(list.scrollHeight + 2, 256);
    const up = below < wanted && above > below;
    const height = Math.max(Math.min(wanted, up ? above : below), 110);
    wrap.classList.toggle('fp-select--up', up);
    list.style.position = 'fixed';
    list.style.left = `${rect.left}px`;
    list.style.right = 'auto';
    list.style.bottom = 'auto';
    list.style.top = `${up ? Math.max(edge, rect.top - gap - height) : rect.bottom + gap}px`;
    list.style.minWidth = `${Math.max(rect.width, floor)}px`;
    list.style.maxHeight = `${height}px`;
  }

  function closeAll(except) {
    document.querySelectorAll('.fp-select.is-open').forEach((wrap) => {
      if (wrap !== except) close(wrap);
    });
  }

  function paint(wrap) {
    const select = wrap.querySelector('select');
    const toggle = wrap.querySelector('.fp-select__toggle');
    const text = toggle.querySelector('.fp-select__value');
    const placeholder = select.selectedOptions[0]?.value === '';
    text.textContent = labelFor(select) || 'Please select';
    toggle.classList.toggle('is-placeholder', placeholder);
    toggle.disabled = select.disabled;
    wrap.querySelectorAll('[role="option"]').forEach((item) => {
      const on = item.dataset.value === select.value;
      item.setAttribute('aria-selected', String(on));
      item.classList.toggle('is-selected', on);
    });
  }

  function choose(wrap, value) {
    const select = wrap.querySelector('select');
    if (select.value !== value) {
      select.value = value;
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    paint(wrap);
    close(wrap, true);
  }

  function move(wrap, delta) {
    const items = [...wrap.querySelectorAll('[role="option"]')];
    if (!items.length) return;
    const active = wrap.querySelector('[role="option"].is-active');
    let index = items.indexOf(active);
    if (index < 0) index = items.findIndex((item) => item.classList.contains('is-selected'));
    index = Math.min(Math.max((index < 0 ? 0 : index) + delta, 0), items.length - 1);
    items.forEach((item) => item.classList.remove('is-active'));
    const target = items[index];
    target.classList.add('is-active');
    target.scrollIntoView({ block: 'nearest' });
    wrap.querySelector('.fp-select__list').setAttribute('aria-activedescendant', target.id);
  }

  function open(wrap) {
    if (isOpen(wrap) || wrap.querySelector('select').disabled) return;
    closeAll(wrap);
    const list = wrap.querySelector('.fp-select__list');
    list.hidden = false;
    wrap.classList.add('is-open');
    wrap.querySelector('.fp-select__toggle').setAttribute('aria-expanded', 'true');
    if (clippingAncestor(wrap)) {
      wrap.classList.add('fp-select--fixed');
      placeList(wrap);
    } else {
      // Flip above the field when there is not enough room below.
      const room = window.innerHeight - wrap.getBoundingClientRect().bottom;
      wrap.classList.toggle('fp-select--up', room < Math.min(list.scrollHeight + 16, 260));
    }
    wrap.querySelectorAll('[role="option"]').forEach((item) => item.classList.remove('is-active'));
    move(wrap, 0);
    list.focus();
  }

  function build(select) {
    if (select.dataset.fpSelectReady) return;
    select.dataset.fpSelectReady = 'true';
    counter += 1;
    const id = `fp-select-${counter}`;

    const wrap = document.createElement('div');
    wrap.className = 'fp-select';
    wrap.dataset.fpSelectUi = 'true';
    select.parentNode.insertBefore(wrap, select);
    wrap.append(select);

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'fp-select__toggle';
    toggle.id = `${id}-toggle`;
    toggle.setAttribute('aria-haspopup', 'listbox');
    toggle.setAttribute('aria-expanded', 'false');
    const label = select.labels?.[0];
    if (label) {
      if (!label.id) label.id = `${id}-label`;
      toggle.setAttribute('aria-labelledby', `${label.id} ${id}-toggle`);
    }
    toggle.innerHTML = '<span class="fp-select__value"></span><svg class="fp-select__caret" viewBox="0 0 12 8" aria-hidden="true"><path d="M1 1.5 6 6.5l5-5"/></svg>';

    const list = document.createElement('ul');
    list.className = 'fp-select__list';
    list.id = `${id}-list`;
    list.tabIndex = -1;
    list.hidden = true;
    list.setAttribute('role', 'listbox');
    if (label?.id) list.setAttribute('aria-labelledby', label.id);
    optionsOf(select).forEach((option, index) => {
      const item = document.createElement('li');
      item.id = `${id}-option-${index}`;
      item.className = 'fp-select__option';
      item.setAttribute('role', 'option');
      item.dataset.value = option.value;
      item.textContent = option.textContent.trim();
      list.append(item);
    });
    toggle.setAttribute('aria-controls', list.id);

    wrap.append(toggle, list);
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    toggle.addEventListener('click', () => (isOpen(wrap) ? close(wrap) : open(wrap)));
    toggle.addEventListener('keydown', (event) => {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        open(wrap);
      }
    });
    list.addEventListener('click', (event) => {
      const item = event.target.closest('[role="option"]');
      if (item) choose(wrap, item.dataset.value);
    });
    list.addEventListener('keydown', (event) => {
      const keys = {
        ArrowDown: () => move(wrap, 1),
        ArrowUp: () => move(wrap, -1),
        Home: () => move(wrap, -999),
        End: () => move(wrap, 999)
      };
      if (keys[event.key]) {
        event.preventDefault();
        keys[event.key]();
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const active = wrap.querySelector('[role="option"].is-active');
        if (active) choose(wrap, active.dataset.value);
      } else if (event.key === 'Escape' || event.key === 'Tab') {
        close(wrap, event.key === 'Escape');
      } else if (event.key.length === 1) {
        // Typeahead, matching the native select.
        const items = [...wrap.querySelectorAll('[role="option"]')];
        const match = items.find((item) => item.textContent.toLowerCase().startsWith(event.key.toLowerCase()));
        if (match) {
          items.forEach((item) => item.classList.remove('is-active'));
          match.classList.add('is-active');
          match.scrollIntoView({ block: 'nearest' });
        }
      }
    });
    list.addEventListener('focusout', (event) => {
      if (!wrap.contains(event.relatedTarget)) close(wrap);
    });
    // Keep the button in step when other code sets the value.
    select.addEventListener('change', () => paint(wrap));

    paint(wrap);
  }

  function enhance(root = document) {
    root.querySelectorAll?.(SELECTOR).forEach(build);
  }

  function reset(root = document) {
    root.querySelectorAll?.('.fp-select').forEach((wrap) => {
      const select = wrap.querySelector('select');
      if (!select) {
        wrap.remove();
        return;
      }
      delete select.dataset.fpSelectReady;
      select.removeAttribute('tabindex');
      select.removeAttribute('aria-hidden');
      wrap.parentNode.insertBefore(select, wrap);
      wrap.remove();
    });
  }

  function refresh(root = document) {
    root.querySelectorAll?.('.fp-select').forEach(paint);
  }

  const repositionOpen = () => {
    document.querySelectorAll('.fp-select.is-open.fp-select--fixed').forEach(placeList);
  };
  window.addEventListener('resize', repositionOpen);
  document.addEventListener('scroll', repositionOpen, true);

  document.addEventListener('click', (event) => {
    if (!event.target.closest('.fp-select')) closeAll();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAll();
  });

  window.FleetPacksSelect = { enhance, reset, refresh };
  const start = () => enhance();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', (event) => enhance(event.target));
})();
